import {
  Icon,
  Status,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@teamsnap/snap-ui';
import { LineItem } from 'components/registration/v2/types';
import { convertFromPennies } from 'core/utils';
import { getFormattedAmount } from 'frontend-toolkit';
import * as React from 'react';

export interface BalanceChangeSummaryProps {
  newLineItems?: LineItem[];
  canceledLineItems?: LineItem[];
  currentBalance: number;
  newBalance: number | undefined;
  lineItemTotal: number;
}

function AmountFormatted({
  amount,
  isCanceled,
}: {
  amount: number;
  isCanceled: boolean;
}) {
  const isReducedFromBalance =
    (isCanceled && amount > 0) || (!isCanceled && amount < 0);
  let formattedAmount = getFormattedAmount(
    convertFromPennies(Math.abs(amount))
  );
  if (isReducedFromBalance) {
    formattedAmount = '-' + formattedAmount;
  } else {
    formattedAmount = '+' + formattedAmount;
  }
  return (
    <span
      data-testid={`BalanceChangeSummary-line-item-amount-${
        isReducedFromBalance ? 'negative' : 'positive'
      }`}
      className={`sui-text-${
        isReducedFromBalance ? 'negative' : 'positive'
      }-text`}
    >
      {formattedAmount}
    </span>
  );
}

export const BalanceChangeSummary = ({
  newLineItems = [],
  canceledLineItems = [],
  currentBalance = 0,
  newBalance,
  lineItemTotal = 0,
}: BalanceChangeSummaryProps) => (
  <div className="sui-flex sui-flex-col sui-gap-1 sui-mb-2 sui-w-7/12">
    <div className="sui-flex sui-flex-row sui-justify-between sui-gap-2">
      <span>Current Balance</span>
      <span data-testid="BalanceChangeSummary-current-balance">
        {getFormattedAmount(convertFromPennies(currentBalance))}
      </span>
    </div>
    {canceledLineItems?.map(({ amount, name, id }, index) => (
      <div
        key={`canceled-${index}`}
        className="sui-flex sui-flex-row sui-justify-between sui-gap-2"
        data-testid="BalanceChangeSummary-line-item"
      >
        <span>{name} (canceled)</span>
        <AmountFormatted amount={amount} isCanceled />
      </div>
    ))}
    {newLineItems?.map(({ amount, name }, index) => (
      <div
        key={`new-${index}`}
        className="sui-flex sui-flex-row sui-justify-between sui-gap-2"
        data-testid="BalanceChangeSummary-line-item"
      >
        <span>{name} (added)</span>
        <AmountFormatted amount={amount} isCanceled={false} />
      </div>
    ))}
    {newBalance !== undefined && (
      <div className="sui-flex sui-flex-row sui-justify-between sui-gap-2 sui-border-t sui-border-solid sui-border-gray-90 sui-py-2">
        <b>New Balance</b>
        <span className="sui-flex sui-gap-1">
          {newBalance && newBalance < 0 && (
            <Status
              state="warning"
              data-testid="BalanceChangeSummary-overpaid-pill"
            >
              <Icon name="running_with_errors" />
              Overpaid
            </Status>
          )}
          {lineItemTotal < 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon
                  className="sui-ml-0.5 sui-text-blue-40"
                  name="info"
                  style={{ marginTop: '0.125rem' }}
                  size="s"
                />
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  Note: Fees cannot decrease by more than $
                  {convertFromPennies(Math.abs(currentBalance))}, as this would
                  make the order total negative. If you save these changes, the
                  order total will be saved as $0.00.
                </p>
              </TooltipContent>
            </Tooltip>
          )}
          <b data-testid="BalanceChangeSummary-new-balance">
            {getFormattedAmount(convertFromPennies(newBalance))}
          </b>
        </span>
      </div>
    )}
  </div>
);
